import type { Gender } from '../athletes';
import { AthleteTag } from '../tags';
import type { ApiAthleteTag } from '../tags';
import type { BudgetGroupAthleteItem } from '../budgetGroups';
import { convertCentsToDollarsDecimal } from '@/utils/helpers';

export class DepthChartAthlete {
  twrn: string;
  fullName: string;
  firstName: string;
  lastName: string;
  avatar: string | null;
  jerseyNumber: string | null;
  isActive: boolean;
  position: string;
  order: number | null;
  rosterSpot: boolean;
  allocation: number;
  scholarshipAllocation: number;
  scholarshipPercentage: number;
  equivalencyPercentage: number;
  scholarshipCalculatedValue: number;
  nilEarnings: number;
  tags: AthleteTag[];
  wasManuallyCreated: boolean;
  removedFromRosterAt: string | null;
  packageTwrn: string | null;
  awardCategories: {
    twrn: string;
    amount: number;
  }[];

  axleTwrn: string | null;
  recruitId: number | null;
  gender: Gender;
  academicYearStart: string | null;
  academicYearEnd: string | null;

  constructor(
    athlete: BudgetGroupAthleteItem,
    budgetTwrn: string,
    tagDefinitions: Record<string, ApiAthleteTag>,
  ) {
    const budgetData = athlete.budgets[budgetTwrn]!;

    const institutionalNilCents = Object.values(budgetData.lineItem?.institutional_nil_categories ?? {})
      .filter((c) => !c.exclude_from_totals)
      .reduce((sum, c) => sum + c.amount, 0);

    const thirdPartyNilCents = Object.values(budgetData.lineItem?.third_party_categories ?? {})
      .filter((c) => !c.exclude_from_totals)
      .reduce((sum, c) => sum + c.amount, 0);

    this.twrn = athlete.twrn;
    this.fullName = athlete.fullName;
    this.firstName = athlete.firstName;
    this.lastName = athlete.lastName;
    this.avatar = athlete.profilePhoto;
    this.jerseyNumber = athlete.jerseyNumber;
    this.isActive = athlete.isActive;
    this.position = athlete.position;
    this.order = budgetData.order;
    this.rosterSpot = budgetData.rosterSpot;
    this.allocation = convertCentsToDollarsDecimal(institutionalNilCents);
    this.scholarshipAllocation = budgetData.totalScholarshipAllocation;
    this.scholarshipPercentage = budgetData.scholarshipPercentage;
    this.equivalencyPercentage = budgetData.equivalencyPercentage;
    this.scholarshipCalculatedValue = budgetData.scholarshipCalculatedValue;
    this.nilEarnings = convertCentsToDollarsDecimal(thirdPartyNilCents);
    this.tags = athlete.tags.reduce<AthleteTag[]>((acc, uuid) => {
      const def = tagDefinitions[uuid];
      if (def) acc.push(new AthleteTag(def));
      return acc;
    }, []);
    this.wasManuallyCreated = athlete.wasManuallyCreated;
    this.removedFromRosterAt = athlete.removedFromRosterAt;
    this.packageTwrn = budgetData.scholarship?.package_twrn ?? null;
    this.awardCategories = budgetData.scholarship?.award_categories ?? [];
    this.axleTwrn = athlete.axleTwrn;
    this.recruitId = athlete.recruitId;
    this.gender = athlete.gender;
    this.academicYearStart = null;
    this.academicYearEnd = null;
  }
}

export class DepthChartPosition {
  athletes: DepthChartAthlete[];
  allocationValue: number;
  allocationPercentage: number;
  allocationSetByUser: boolean;
  athleteCount: number;
  budgetRemaining: number;
  committed: number;
  position: string;
  rosterSize: number;
  totalScholarships: number;
  totalBudget: number;
  totalScholarshipsAllocation: number;
  totalNilEarnings: number;
  budgetAllocated: number;
  totalScholarshipsNew: number;
  totalScholarshipsNewAllocation: number;

  constructor(
    athletes: DepthChartAthlete[],
    positionName: string,
    allocationValue: number,
    totalTeamBudget: number,
    allocationSetByUser: boolean,
  ) {
    const committed = athletes.reduce((sum, a) => sum + a.allocation, 0);

    this.athletes = athletes;
    this.position = positionName;
    this.allocationValue = allocationValue;
    this.allocationPercentage = totalTeamBudget > 0 ? (allocationValue / totalTeamBudget) * 100 : 0;
    this.allocationSetByUser = allocationSetByUser;
    this.athleteCount = athletes.length;
    this.committed = committed;
    this.budgetRemaining = allocationValue - committed;
    this.budgetAllocated = committed;
    this.rosterSize = athletes.filter((a) => a.rosterSpot).length;
    this.totalBudget = totalTeamBudget;
    this.totalScholarships = athletes.reduce((sum, a) => sum + a.scholarshipPercentage, 0);
    this.totalScholarshipsAllocation = athletes.reduce((sum, a) => sum + a.scholarshipAllocation, 0);
    this.totalNilEarnings = athletes.reduce((sum, a) => sum + a.nilEarnings, 0);
    this.totalScholarshipsNew = athletes.reduce((sum, a) => sum + a.equivalencyPercentage, 0);
    this.totalScholarshipsNewAllocation = athletes.reduce((sum, a) => sum + a.scholarshipCalculatedValue, 0) / 100;
  }
}


export interface ApiStoreTeamBudgetGroupAthlete {
  twrn: string;
  first_name: string;
  last_name: string;
  position_id: string | null;
  has_roster_spot: boolean;
  allocation: number;
  scholarship_percentage: number;
  scholarship_allocation: number;
  nil_earnings: number;
  recruit_id: number | null;
  email: string;
}

export interface ApiStoreTeamBudgetGroupAthleteRequest {
  first_name: string;
  last_name: string;
  position_id?: string | null;
  has_roster_spot: boolean;
  allocation?: number;
  scholarship_percentage?: number;
  scholarship_allocation?: number;
  nil_earnings?: number;
  recruit_id?: number | null;
  email?: string | null;
  budget_twrns: string[];
}

export interface ApiStoreTeamBudgetGroupAthleteResponse {
  team_twrn: string;
  budget_group_twrn: string;
  budget_twrns: string[];
  athlete: ApiStoreTeamBudgetGroupAthlete;
}

export interface StoreTeamBudgetGroupAthleteResponse {
  teamTwrn: string;
  budgetGroupTwrn: string;
  budgetTwrns: string[];
  athlete: BudgetGroupAthlete;
}

export class BudgetGroupAthlete {
  twrn: string;
  firstName: string;
  lastName: string;
  positionId: string | null;
  hasRosterSpot: boolean;
  allocation: number;
  scholarshipPercentage: number;
  scholarshipAllocation: number;
  nilEarnings: number;
  recruitId: number | null;
  email: string;

  constructor(data: ApiStoreTeamBudgetGroupAthlete) {
    this.twrn = data.twrn;
    this.firstName = data.first_name;
    this.lastName = data.last_name;
    this.positionId = data.position_id;
    this.hasRosterSpot = data.has_roster_spot;
    this.allocation = data.allocation;
    this.scholarshipPercentage = data.scholarship_percentage;
    this.scholarshipAllocation = data.scholarship_allocation;
    this.nilEarnings = data.nil_earnings;
    this.recruitId = data.recruit_id;
    this.email = data.email;
  }

  static fromApi(data: ApiStoreTeamBudgetGroupAthlete): BudgetGroupAthlete {
    return new BudgetGroupAthlete(data);
  }
}
